import { useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatMoney } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Sparkles, CreditCard, Check } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const STATUS_MAP = {
  trialing: { label: "Free trial", tone: "safe" },
  active: { label: "Active", tone: "safe" },
  free: { label: "Free — no monthly fees", tone: "safe" },
  past_due: { label: "Past due", tone: "tight" },
  canceled: { label: "Canceled", tone: "short" },
  expired: { label: "Expired", tone: "short" },
  none: { label: "None", tone: "tight" },
};

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/subscription/me");
    setSub(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      track("subscription_checkout_started", { current_status: sub?.status });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setStarting(false);
    }
  };

  if (loading) return <div className="text-[#59605C]">Loading…</div>;

  const meta = STATUS_MAP[sub.status] || STATUS_MAP.none;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Subscription</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Your plan</h1>
      </div>

      <div className="card-soft p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-heading text-2xl font-medium text-[#2C302E]">Monthly plan</div>
              <StatusBadge status={meta.tone} testId="subscription-status" />
            </div>
            <div className="text-sm text-[#59605C] mt-1">
              {sub.status === "trialing" && (
                <>Free trial · {sub.trial_days_remaining} days remaining · Ends {formatDate(sub.trial_end)}</>
              )}
              {sub.status === "active" && (
                <>Active · next renewal on {formatDate(sub.current_period_end)}</>
              )}
              {sub.status === "free" && (
                <>No monthly fees · your Free account never expires</>
              )}
              {sub.status !== "trialing" && sub.status !== "active" && sub.status !== "free" && meta.label}
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-medium text-[#2C302E]">{formatMoney(sub.monthly_price_usd)}<span className="text-sm text-[#8A918D] font-normal">/mo</span></div>
            <div className="text-xs text-[#8A918D]">Billed monthly · Cancel anytime</div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-3">
          <Bullet>Unlimited bills, income, and pay periods</Bullet>
          <Bullet>Weekly, biweekly, monthly pay period planning</Bullet>
          <Bullet>Automatic carryover for unpaid balances</Bullet>
          <Bullet>Safe / Tight / Short live status</Bullet>
          <Bullet>Sample demo dataset any time</Bullet>
          <Bullet>Cancel from this page in one click</Bullet>
        </div>

        <div className="mt-8 flex gap-3 flex-wrap">
          {sub.status === "trialing" && (
            <button onClick={startCheckout} className="btn-primary" disabled={starting} data-testid="subscription-start-checkout">
              <CreditCard size={16} /> {starting ? "Starting checkout…" : "Subscribe now — keep trial too"}
            </button>
          )}
          {(sub.status === "canceled" || sub.status === "expired" || sub.status === "past_due") && (
            <button onClick={startCheckout} className="btn-primary" disabled={starting} data-testid="subscription-reactivate">
              <CreditCard size={16} /> {starting ? "…" : "Reactivate subscription"}
            </button>
          )}
          {sub.status === "active" && (
            <div className="rounded-2xl status-safe px-4 py-3 text-sm flex items-center gap-2">
              <Sparkles size={16} /> You’re all set — thanks for supporting us.
            </div>
          )}
          {sub.status === "free" && (
            <div className="rounded-2xl status-safe px-4 py-3 text-sm flex items-center gap-2" data-testid="subscription-free-badge">
              <Sparkles size={16} /> Congratulations! You've been approved for a FREE account (No monthly fees).
            </div>
          )}
        </div>
      </div>

      <div className="card-soft p-6 text-sm text-[#59605C]" data-testid="subscription-help">
        <div className="font-heading text-base font-medium text-[#2C302E] mb-1">Need to cancel?</div>
        Your access continues until the end of your billing period. Contact us any time for help.
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2 text-sm text-[#2C302E]">
      <Check size={16} className="text-[#4A6148] mt-0.5 shrink-0" /> {children}
    </div>
  );
}
